import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import AccessGate from "./components/AccessGate";
import TopBar from "./components/TopBar";
import MasterAtlas from "./components/MasterAtlas";
import RegionViewer from "./components/RegionViewer";
import ExplorePanel from "./components/ExplorePanel";
import ViewAll from "./components/ViewAll";
import AboutPanel from "./components/AboutPanel";
import MasterPlan from "./components/MasterPlan";
import Signature from "./components/Signature";
import { regionById } from "./data/regions";

function IntroReveal({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3400);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <motion.div
      data-testid="intro-reveal"
      className="fixed inset-0 z-[75] flex items-center justify-center cursor-pointer"
      style={{ background: "rgba(10,9,7,0.55)", backdropFilter: "blur(3px)" }}
      onClick={onDone}
      exit={{ opacity: 0, transition: { duration: 1.1, ease: "easeOut" } }}
    >
      <div className="text-center px-6">
        <span className="mask-line">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.2, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="block font-display uppercase text-[#E8DCC4] text-4xl sm:text-5xl lg:text-6xl tracking-[0.35em] pl-3"
          >
            Bharat
          </motion.span>
        </span>
        <span className="mask-line mt-4">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{ delay: 0.5, duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="block font-serif-body italic text-[#C5A059] text-lg sm:text-xl"
          >
            A Nation. A Thousand Experiences.
          </motion.span>
        </span>
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.9, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="h-px w-40 mx-auto mt-8 bg-[rgba(197,160,89,0.4)]"
        />
      </div>
    </motion.div>
  );
}

export default function App() {
  const [unlocked, setUnlocked] = useState(
    () => localStorage.getItem("mip-access") === "granted"
  );
  const [intro, setIntro] = useState(false);
  const [overlay, setOverlay] = useState(null); // explore | viewall | about
  const [viewer, setViewer] = useState(null); // { regionId, imageIndex }

  const openRegion = (id, imageIndex = 0) => {
    setOverlay(null);
    setViewer({ regionId: id, imageIndex });
  };

  const handleAwaiting = (region) => {
    toast(`${region.name}`, {
      description: "This region awaits its artwork — it will join the atlas soon.",
    });
  };

  const handleNav = (target) => {
    setViewer(null);
    setOverlay(target === "atlas" ? null : target);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !viewer) setOverlay(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewer]);

  return (
    <div className="h-full" data-testid="app-root">
      <AnimatePresence>
        {!unlocked && (
          <AccessGate
            key="gate"
            onUnlock={() => {
              setUnlocked(true);
              setIntro(true);
            }}
          />
        )}
      </AnimatePresence>

      {unlocked && (
        <>
          <TopBar onNav={handleNav} />
          <MasterAtlas onOpenRegion={openRegion} onAwaiting={handleAwaiting} />

          <AnimatePresence>
            {intro && <IntroReveal key="intro" onDone={() => setIntro(false)} />}
          </AnimatePresence>

          <AnimatePresence>
            {overlay === "explore" && (
              <ExplorePanel
                key="explore"
                onSelect={(id) => openRegion(id)}
                onClose={() => setOverlay(null)}
              />
            )}
            {overlay === "viewall" && (
              <ViewAll
                key="viewall"
                onSelect={(id) => openRegion(id)}
                onClose={() => setOverlay(null)}
              />
            )}
            {overlay === "about" && (
              <AboutPanel key="about" onClose={() => setOverlay(null)} />
            )}
            {overlay === "plan" && (
              <MasterPlan key="plan" onClose={() => setOverlay(null)} />
            )}
          </AnimatePresence>

          <AnimatePresence>
            {viewer && regionById(viewer.regionId) && (
              <RegionViewer
                key="viewer"
                regionId={viewer.regionId}
                imageIndex={viewer.imageIndex}
                onClose={() => setViewer(null)}
                onNavigate={(id, idx) => setViewer({ regionId: id, imageIndex: idx })}
                onExplore={() => {
                  setViewer(null);
                  setOverlay("explore");
                }}
              />
            )}
          </AnimatePresence>
        </>
      )}

      <Signature />
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#161310",
            border: "1px solid rgba(197,160,89,0.25)",
            color: "#D6C5B3",
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: "1rem",
            borderRadius: 0,
            marginBottom: "3.5rem",
          },
        }}
      />
    </div>
  );
}
